import React, { useState, useEffect } from "react";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import CustomDropdown from "../../components/CustomDropdown";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import dropdownOptions from "../../data/dropdownOptions.json";
import { fetchAndStoreServerPublicKey } from "../../utils/apiClient";

export default function InformationAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState(null);
  const [placeOfBirth, setPlaceOfBirth] = useState("");
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [gender, setGender] = useState(null);
  const [isAmericanCitizen, setIsAmericanCitizen] = useState(false);
  const [spouseFirstName, setSpouseFirstName] = useState("");
  const [spouseProfession, setSpouseProfession] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneDialCode, setPhoneDialCode] = useState(null);
  const [phoneFullDigits, setPhoneFullDigits] = useState("");
  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [phoneInputCountry, setPhoneInputCountry] = useState("mu");

  // Initial phone props restored from storage.
  const [initialPhoneProps] = useState(() => {
    try {
      const saved = sessionStorage.getItem('registration_step2_data')
        || localStorage.getItem('registration_step2_data');
      if (saved) {
        const data = JSON.parse(saved);
        const fullDigits = (data.phoneFullDigits || "").toString().replace(/\D/g, "");
        const dial = data.phoneDialCode || null;
        const num = data.phoneNumber || "";
        const countryCode = data.phoneCountry
          ? (data.phoneCountry.countryCode || data.phoneCountry.iso2 || "mu").toLowerCase()
          : "mu";
        return {
          country: countryCode,
          value: fullDigits
            ? `+${fullDigits}`
            : (dial && num ? `+${dial}${num}` : "")
        };
      }
    } catch (e) { /* ignore */ }
    return { country: "mu", value: "" };
  });
  const [errors, setErrors] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);
  const [accountType, setAccountType] = useState(null);
  const [userType, setUserType] = useState(null);

  // Initialize controlled phone input state once from restored initial props.
  useEffect(() => {
    setPhoneInputCountry(initialPhoneProps.country || "mu");
    setPhoneInputValue((initialPhoneProps.value || "").toString().replace(/\D/g, ""));
  }, [initialPhoneProps]);

  // Get account type and user type from location state or sessionStorage
  useEffect(() => {
    // PRIORITY 1: Check location.state FIRST - if it exists, it's definitely programmatic navigation
    // This is the most reliable indicator of programmatic navigation vs refresh
    const stateData = location.state || {};
    const hasStateData = stateData.accountType || stateData.userType;
    
    // If we have location.state, it's definitely programmatic navigation (not a refresh)
    // Skip Performance API check entirely in this case
    if (hasStateData) {
      // Continue with normal flow below
    } else {
      // No location.state - check Performance API to see if it's a refresh
      let isPageRefresh = false;
      try {
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        if (navigationEntry) {
          // 'reload' means user pressed F5 or refresh button
          isPageRefresh = navigationEntry.type === 'reload';
        }
      } catch (e) {
        // Performance API not available, try alternative method
        try {
          if (performance.navigation && performance.navigation.type === 1) {
            isPageRefresh = true; // TYPE_RELOAD = 1
          }
        } catch (e2) {
        }
      }
      
      // If it's a refresh (and no location.state), clear all data and redirect to step 1
      if (isPageRefresh) {
        // Clear all session storage
        sessionStorage.removeItem('registration_step1_data');
        sessionStorage.removeItem('registration_step2_data');
        localStorage.removeItem('registration_accountType');
        localStorage.removeItem('registration_userType');
        // Redirect to step 1 with clean state
        navigate("/informationAccount", { replace: true });
        return;
      }
    }
    
    // If not a refresh, check if we have location.state (programmatic navigation)
    // stateData and hasStateData are already declared above
    let savedStep1Data = sessionStorage.getItem('registration_step1_data');
    
    // If we have location.state, it's programmatic navigation (not a refresh)
    if (hasStateData) {
    }
    
    // If we have state data but no saved data yet, save it now (navigation just happened)
    // This can happen if there's a timing issue or if step 1 didn't save properly
    if (hasStateData && !savedStep1Data) {
      // Create a minimal entry - leverage and brokerId should have been saved in step 1
      // If they weren't, we'll use null and they'll need to go back
      const registrationData = {
        accountType: stateData.accountType,
        userType: stateData.userType,
        leverage: null, // Should have been set in step 1
        brokerId: null,  // Should have been set in step 1
        timestamp: Date.now() // Add timestamp
      };
      sessionStorage.setItem('registration_step1_data', JSON.stringify(registrationData));
      savedStep1Data = sessionStorage.getItem('registration_step1_data');
    } else if (hasStateData && savedStep1Data) {
      // Update timestamp if we have both state and saved data (fresh navigation)
      try {
        const step1Data = JSON.parse(savedStep1Data);
        step1Data.timestamp = Date.now();
        sessionStorage.setItem('registration_step1_data', JSON.stringify(step1Data));
      } catch (e) {
        // Failed to parse, ignore
      }
    }
    
    // If we don't have state data and no saved data, redirect to step 1
    // But only if we're sure this isn't a fresh navigation with state
    if (!hasStateData && !savedStep1Data) {
      navigate("/informationAccount");
      return;
    }
    
    // Restore step 1 data (accountType, userType, leverage, brokerId)
    if (savedStep1Data) {
      try {
        const step1Data = JSON.parse(savedStep1Data);
        if (step1Data.accountType) {
          setAccountType(step1Data.accountType);
        }
        if (step1Data.userType) {
          setUserType(step1Data.userType);
        }
      } catch (e) {
        // Failed to parse
      }
    }
    
    // Fallback to location state or localStorage
    const storedAccountType = stateData.accountType || localStorage.getItem('registration_accountType');
    const storedUserType = stateData.userType || localStorage.getItem('registration_userType');
    
    if (storedAccountType) {
      setAccountType(storedAccountType);
    }
    if (storedUserType) {
      setUserType(storedUserType);
    }

    // Restore form data from sessionStorage (when navigating back, not on refresh)
    const savedFormData = sessionStorage.getItem('registration_step2_data');
    if (savedFormData) {
      try {
        const formData = JSON.parse(savedFormData);
        // Restore all fields if they exist
        if (formData.firstName !== undefined && formData.firstName !== null) setFirstName(formData.firstName);
        if (formData.fatherName !== undefined && formData.fatherName !== null) setFatherName(formData.fatherName);
        if (formData.motherName !== undefined && formData.motherName !== null) setMotherName(formData.motherName);
        if (formData.lastName !== undefined && formData.lastName !== null) setLastName(formData.lastName);
        if (formData.dob) setDob(new Date(formData.dob));
        if (formData.placeOfBirth !== undefined && formData.placeOfBirth !== null) setPlaceOfBirth(formData.placeOfBirth);
        if (formData.maritalStatus !== undefined && formData.maritalStatus !== null) setMaritalStatus(formData.maritalStatus);
        if (formData.gender !== undefined && formData.gender !== null) setGender(formData.gender);
        if (formData.isAmericanCitizen !== undefined) setIsAmericanCitizen(formData.isAmericanCitizen);
        if (formData.spouseFirstName !== undefined && formData.spouseFirstName !== null) setSpouseFirstName(formData.spouseFirstName);
        if (formData.spouseProfession !== undefined && formData.spouseProfession !== null) setSpouseProfession(formData.spouseProfession);
        if (formData.phoneNumber !== undefined && formData.phoneNumber !== null) setPhoneNumber(formData.phoneNumber);
        if (formData.phoneCountry !== undefined && formData.phoneCountry !== null) setPhoneCountry(formData.phoneCountry);
        if (formData.phoneDialCode !== undefined && formData.phoneDialCode !== null) setPhoneDialCode(formData.phoneDialCode);
        if (formData.phoneFullDigits !== undefined && formData.phoneFullDigits !== null) {
          const restoredFullDigits = formData.phoneFullDigits.toString().replace(/\D/g, "");
          setPhoneFullDigits(restoredFullDigits);
          setPhoneInputValue(restoredFullDigits);
        } else {
          // Backward compatibility with old saved data structure.
          const restoredDial = (formData.phoneDialCode || (formData.phoneCountry && formData.phoneCountry.dialCode) || "").toString().replace(/\D/g, "");
          const restoredNational = (formData.phoneNumber || "").toString().replace(/\D/g, "");
          const restoredFullDigits = `${restoredDial}${restoredNational}`;
          setPhoneFullDigits(restoredFullDigits);
          setPhoneInputValue(restoredFullDigits);
        }
        if (formData.phoneCountry && (formData.phoneCountry.countryCode || formData.phoneCountry.iso2)) {
          setPhoneInputCountry((formData.phoneCountry.countryCode || formData.phoneCountry.iso2).toLowerCase());
        }
      } catch (e) {
        // Failed to restore form data
      }
    }

    // Initialize server public key
    const initializeServerKey = async () => {
      try {
        await fetchAndStoreServerPublicKey();
      } catch (error) {
        // Silent fail
      }
    };
    initializeServerKey();
  }, [location, navigate]);

  // Save form data to sessionStorage and localStorage whenever it changes
  useEffect(() => {
    const formData = {
      firstName,
      fatherName,
      motherName,
      lastName,
      dob: dob ? dob.toISOString() : null,
      placeOfBirth,
      maritalStatus,
      gender,
      isAmericanCitizen,
      spouseFirstName,
      spouseProfession,
      phoneNumber,
      phoneCountry,
      phoneDialCode,
      phoneFullDigits
    };
    // Save to both sessionStorage (for current session) and localStorage (as backup)
    sessionStorage.setItem('registration_step2_data', JSON.stringify(formData));
    localStorage.setItem('registration_step2_data', JSON.stringify(formData));
  }, [firstName, fatherName, motherName, lastName, dob, placeOfBirth, maritalStatus, gender, isAmericanCitizen, spouseFirstName, spouseProfession, phoneNumber, phoneCountry, phoneDialCode, phoneFullDigits]);

  const maritalStatusOptions = (dropdownOptions.maritalStatusOptions || []).map((opt) => ({
    ...opt,
    label:
      t(`personalInformation.maritalStatus.options.${opt.value}`) !==
      `personalInformation.maritalStatus.options.${opt.value}`
        ? t(`personalInformation.maritalStatus.options.${opt.value}`)
        : opt.label,
  }));

  const genderOptions = (dropdownOptions.genderOptions || []).map((opt) => ({
    ...opt,
    label:
      t(`personalInformation.gender.options.${opt.value}`) !==
      `personalInformation.gender.options.${opt.value}`
        ? t(`personalInformation.gender.options.${opt.value}`)
        : opt.label,
  }));


  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = t("personalInformation.errors.firstName");
    if (!lastName.trim()) newErrors.lastName = t("personalInformation.errors.lastName");
    if (!fatherName.trim()) newErrors.fatherName = t("personalInformation.errors.fatherName");
    if (!motherName.trim()) newErrors.motherName = t("personalInformation.errors.motherName");
    if (!dob) {
      newErrors.dob = t("personalInformation.errors.dob");
    } else {
      // Validate age is at least 18
      const today = new Date();
      const birthDate = new Date(dob);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        newErrors.dob = t("personalInformation.errors.dobAge") || "You must be at least 18 years old";
      }
    }
    if (!placeOfBirth.trim()) newErrors.placeOfBirth = t("personalInformation.errors.placeOfBirth");
    if (!gender) newErrors.gender = t("personalInformation.errors.gender");
    if (!maritalStatus) newErrors.maritalStatus = t("personalInformation.errors.maritalStatus");
    const maritalValue = maritalStatus ? (typeof maritalStatus === "object" ? maritalStatus.value : maritalStatus) : null;
    const isMarriedCheck = maritalValue && maritalValue.toLowerCase() === "married";
    if (isMarriedCheck && !spouseFirstName.trim()) newErrors.spouseFirstName = t("personalInformation.errors.spouseFirstName");
    if (isMarriedCheck && !spouseProfession.trim()) newErrors.spouseProfession = t("personalInformation.errors.spouseProfession");
    if (!phoneNumber || phoneNumber.trim() === "") {
      newErrors.phoneNumber = t("personalInformation.errors.phoneNumber");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateForm()) {
      return;
    }

    if (isAmericanCitizen) {
      setShowBlockedPopup(true);
      return;
    }

    navigate("/registrationEmail");
  };

  const getStepOneBackPath = () => {
    try {
      const savedStep1Data = sessionStorage.getItem('registration_step1_data');
      if (savedStep1Data) {
        const step1Data = JSON.parse(savedStep1Data);
        const savedBrokerId = step1Data?.brokerId;
        if (savedBrokerId !== undefined && savedBrokerId !== null && `${savedBrokerId}`.trim() !== "") {
          return `/informationAccount/${savedBrokerId}`;
        }
      }
    } catch (e) {
      // Fall back to step 1 without broker id if storage is malformed.
    }
    return "/informationAccount";
  };

  // Show success page if verification is complete
  return (
    <div className="page-container">
      <CustomCard className="registration-card">
        <div className="signup-content">
          <h1 className="signup-title">{t("personalInformation.title")}</h1>

          {/* Progress */}
          <div className="progress-indicator">
            <div className="progress-circle active">
              {t("informationAccount.progress.step1")}
            </div>
            <div className="progress-line progress-line-active" />
            <div className="progress-circle active">
              {t("informationAccount.progress.step2")}
            </div>
            <div className="progress-line" />
            <div className="progress-circle">
              {t("informationAccount.progress.step3")}
            </div>
          </div>

          <div className="signup-form">
            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("personalInformation.fields.firstName")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-user"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("personalInformation.placeholders.firstName")}
                    className={`input-box ${errors.firstName ? "input-error" : ""}`}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) {
                        setErrors({ ...errors, firstName: null });
                      }
                    }}
                  />
                </div>
              </div>

              <div className="form-group col-6">
                <label>{t("personalInformation.fields.lastName")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-user"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("personalInformation.placeholders.lastName")}
                    className={`input-box ${errors.lastName ? "input-error" : ""}`}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (errors.lastName) {
                        setErrors({ ...errors, lastName: null });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-6">
                <label>{t("personalInformation.fields.fatherName")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-user-tie"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("personalInformation.placeholders.fatherName")}
                    className={`input-box ${errors.fatherName ? "input-error" : ""}`}
                    value={fatherName}
                    onChange={(e) => {
                      setFatherName(e.target.value);
                      if (errors.fatherName) {
                        setErrors({ ...errors, fatherName: null });
                      }
                    }}
                  />
                </div>
              </div>

              <div className="form-group col-6">
                <label>{t("personalInformation.fields.motherName")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-user"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("personalInformation.placeholders.motherName")}
                    className={`input-box ${errors.motherName ? "input-error" : ""}`}
                    value={motherName}
                    onChange={(e) => {
                      setMotherName(e.target.value);
                      if (errors.motherName) {
                        setErrors({ ...errors, motherName: null });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-row date-birth-row">
              <div className="form-group col-6 date-of-birth-field">
                <label>{t("personalInformation.fields.dateOfBirth")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-calendar-days"></i>
                  </span>
                  <DatePicker
                    selected={dob}
                    onChange={(date) => {
                      setDob(date);
                      if (errors.dob) {
                        setErrors({ ...errors, dob: null });
                      }
                    }}
                    placeholderText={t("personalInformation.placeholders.dateOfBirth")}
                    className={`input-box ${errors.dob ? "input-error" : ""}`}
                    dateFormat="dd/MM/yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                    withPortal
                  />
                </div>
              </div>

              <div className="form-group col-6">
                <label>{t("personalInformation.fields.placeOfBirth")}</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <i className="fa-solid fa-location-dot"></i>
                  </span>
                  <input
                    type="text"
                    placeholder={t("personalInformation.placeholders.placeOfBirth")}
                    className={`input-box ${errors.placeOfBirth ? "input-error" : ""}`}
                    value={placeOfBirth}
                    onChange={(e) => {
                      setPlaceOfBirth(e.target.value);
                      if (errors.placeOfBirth) {
                        setErrors({ ...errors, placeOfBirth: null });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-row gender-marital-row">
              <div className="form-group col-6">
                <label>{t("personalInformation.fields.gender")}</label>
                <CustomDropdown
                  options={genderOptions}
                  value={gender}
                  onChange={(value) => {
                    setGender(value);
                    if (errors.gender) {
                      setErrors({ ...errors, gender: null });
                    }
                  }}
                  placeholder={t("personalInformation.placeholders.selectGender")}
                  className={errors.gender ? "dropdown-error" : ""}
                />
              </div>
              <div className="form-group col-6">
                <label>{t("personalInformation.fields.maritalStatus")}</label>
                <CustomDropdown
                  options={maritalStatusOptions}
                  value={maritalStatus}
                  onChange={(value) => {
                    setMaritalStatus(value);
                    if (errors.maritalStatus) {
                      setErrors({ ...errors, maritalStatus: null });
                    }
                  }}
                  placeholder={t("personalInformation.placeholders.selectMaritalStatus")}
                  className={errors.maritalStatus ? "dropdown-error" : ""}
                />
              </div>
            </div>

            {(maritalStatus && (typeof maritalStatus === "object" ? maritalStatus.value : maritalStatus).toLowerCase() === "married") && (
              <div className="form-row">
                <div className="form-group col-6">
                  <label>{t("personalInformation.fields.spouseFirstName")}</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <i className="fa-solid fa-user"></i>
                    </span>
                    <input
                      type="text"
                      placeholder={t("personalInformation.placeholders.spouseFirstName")}
                      className={`input-box ${errors.spouseFirstName ? "input-error" : ""}`}
                      value={spouseFirstName}
                      onChange={(e) => {
                        setSpouseFirstName(e.target.value);
                        if (errors.spouseFirstName) {
                          setErrors({ ...errors, spouseFirstName: null });
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="form-group col-6">
                  <label>{t("personalInformation.fields.spouseProfession")}</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <i className="fa-solid fa-briefcase"></i>
                    </span>
                    <input
                      placeholder={t("personalInformation.placeholders.spouseProfession")}
                      className={`input-box ${errors.spouseProfession ? "input-error" : ""}`}
                      value={spouseProfession}
                      onChange={(e) => {
                        setSpouseProfession(e.target.value);
                        if (errors.spouseProfession) {
                          setErrors({ ...errors, spouseProfession: null });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group col-12 radio-inline">
                <label className="radio-label">
                  {t("personalInformation.fields.americanCitizen")}
                </label>

                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="americanCitizen"
                      value="no"
                      checked={isAmericanCitizen === false}
                      onChange={() => setIsAmericanCitizen(false)}
                    />
                    <span className="custom-radio" />
                    <span className="radio-text">{t("personalInformation.radioOptions.no")}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="americanCitizen"
                      value="yes"
                      checked={isAmericanCitizen === true}
                      onChange={() => setIsAmericanCitizen(true)}
                    />
                    <span className="custom-radio" />
                    <span className="radio-text">{t("personalInformation.radioOptions.yes")}</span>
                  </label>
                </div>
              </div>
            </div>


            <div className="form-group col-12">
              <label>{t("personalInformation.fields.phoneNumber")}</label>
              <div className={`phone-input-wrapper ${errors.phoneNumber ? "input-error" : ""}`}>
                <PhoneInput
                  country={phoneInputCountry}
                  value={phoneInputValue}
                  // Prevent auto-switching country based on typed digits.
                  disableCountryGuess
                  onChange={(value, country) => {
                    const dialCode = country && country.dialCode ? country.dialCode : "";

                    // "value" from react-phone-input-2 includes dial code + national digits (no "+")
                    // Strip the dial code to get ONLY the national number (matching Flutter's _phoneNumberwithoutcode)
                    const fullDigits = (value || "").replace(/\D/g, "");
                    let nationalNumber = fullDigits;
                    if (dialCode && fullDigits.startsWith(dialCode)) {
                      nationalNumber = fullDigits.substring(dialCode.length);
                    }

                    setPhoneInputValue(fullDigits);
                    setPhoneInputCountry(country && (country.countryCode || country.iso2)
                      ? (country.countryCode || country.iso2).toLowerCase()
                      : phoneInputCountry);
                    setPhoneFullDigits(fullDigits);      // Store complete number digits (dial + national)
                    setPhoneNumber(nationalNumber);   // Store national digits only (without dial code)
                    setPhoneCountry(country || null);
                    setPhoneDialCode(dialCode);       // Store selected country's dial code

                    if (errors.phoneNumber) {
                      setErrors({ ...errors, phoneNumber: null });
                    }
                  }}
                  enableSearch
                  excludeCountries={['il']}
                />
              </div>
            </div>

            <div className="button-group-vertical">
              <CustomButton 
                variant="primary"
                className="next-button" 
                onClick={handleNext} 
                disabled={isAmericanCitizen}
              >
                {t("informationAccount.nextButton")}
              </CustomButton>
              <CustomButton 
                variant="secondary"
                className="back-button" 
                onClick={() => {
                  // Ensure step 2 data is saved before going back
                  const formData = {
                    firstName,
                    fatherName,
                    motherName,
                    lastName,
                    dob: dob ? dob.toISOString() : null,
                    placeOfBirth,
                    maritalStatus,
                    gender,
                    isAmericanCitizen,
                    spouseFirstName,
                    spouseProfession,
                    phoneNumber,
                    phoneCountry,
                    phoneDialCode,
                    phoneFullDigits
                  };
                  sessionStorage.setItem('registration_step2_data', JSON.stringify(formData));
                  // Navigate back to step 1 - data will be restored from sessionStorage
                  navigate(getStepOneBackPath());
                }}
              >
                {t("common.back") || "Back"}
              </CustomButton>
            </div>
          </div>
        </div>
      </CustomCard>

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title="Error"
        message={errorPopupMessage}
        buttonText="OK"
        icon="fa-circle-exclamation"
      />

      <Popup
        isOpen={showBlockedPopup}
        onClose={() => setShowBlockedPopup(false)}
        title="Account Blocked"
        message="The account has been blocked. American citizens are not eligible for this service."
        buttonText="OK"
        icon="fa-ban"
      />
    </div>
  );
}
